import { useCallback, useEffect, useRef, useState } from 'react';
import { useUi } from '../store';
import type { GameHandle } from '../game/useGame';

/** How long you hold before the wave commits. */
const HOLD_MS = 700;
/** How far the thumb may wander mid-hold before it counts as backing out. */
const SLIP_PX = 40;

/**
 * The commit control, and the reason you'd want to commit.
 *
 * It's hold-to-start rather than tap: launching a wave before you're ready is
 * the one irreversible thing on this screen, and a stray thumb shouldn't do it.
 *
 * What's coming is drawn on the playfield instead of here, as arrows on the
 * bearings the wave will actually arrive from. A row of chips told you what was
 * coming but not where, and where is the half that decides where you build.
 */
export default function WaveStart({ game }: { game: GameHandle | null }): JSX.Element | null {
  const hud = useUi((s) => s.hud);
  const sheet = useUi((s) => s.sheet);
  const [holding, setHolding] = useState(false);
  const timer = useRef<number | undefined>(undefined);
  const from = useRef<{ x: number; y: number } | null>(null);

  const cancel = useCallback(() => {
    setHolding(false);
    from.current = null;
    if (timer.current !== undefined) window.clearTimeout(timer.current);
    timer.current = undefined;
  }, []);

  useEffect(() => cancel, [cancel]);

  if (!game || !hud.building) return null;
  // The sheet and this both live at the bottom; one at a time.
  if (sheet !== null) return null;
  // The opening level holds the button back until something is mounted, so a
  // first-timer can't start a wave with a bare planet. The coach is showing
  // them where to build in the meantime.
  if (!hud.canStart) return null;

  const begin = (ev: React.PointerEvent): void => {
    ev.currentTarget.setPointerCapture(ev.pointerId);
    from.current = { x: ev.clientX, y: ev.clientY };
    setHolding(true);
    timer.current = window.setTimeout(() => {
      cancel();
      game.input.push({ kind: 'start-wave' });
    }, HOLD_MS);
  };

  /**
   * Sliding your thumb away mid-hold backs out — the escape hatch that makes
   * hold-to-commit safe.
   *
   * It's a distance check rather than `onPointerLeave`, which is what this used
   * and which never fired: `begin` calls `setPointerCapture`, and a captured
   * pointer sends every subsequent boundary event to the capture target no
   * matter where it actually is. So the button never saw the thumb leave, and
   * the abort gesture silently did nothing — you could press, slide right off
   * the control, and the wave still went. Measuring from where the press
   * started doesn't care about capture.
   */
  const drift = (ev: React.PointerEvent): void => {
    const start = from.current;
    if (!start) return;
    if (Math.hypot(ev.clientX - start.x, ev.clientY - start.y) > SLIP_PX) cancel();
  };

  return (
    <button
      className={`start-cta${holding ? ' is-holding' : ''}`}
      onPointerDown={begin}
      onPointerMove={drift}
      onPointerUp={cancel}
      onPointerCancel={cancel}
    >
      <span
        className="start-fill"
        style={{ transitionDuration: holding ? `${HOLD_MS}ms` : '120ms' }}
      />
      <span className="start-label">Hold to start</span>
    </button>
  );
}
